/**
 * 现场发码：现场工程师的快速查询台。
 *
 * 设计目标：选中一台设备后，立刻拿到仪器激活码或维护动态密码，不必进入设备详情页。
 * 两种码都基于同一份设备密钥的 TOTP：仪器激活码 7 位（按阶段派生），维护动态密码 6 位，
 * 都以 60 秒为一个窗口切换，因此界面上的倒计时与「当前窗口」是一等公民。
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Check,
  CircleCheck,
  CircleX,
  Copy,
  Info,
  KeyRound,
  Loader2,
  RefreshCw,
  ScanLine,
  Smartphone,
  Timer,
  TriangleAlert,
} from 'lucide-react'
import { toast } from 'sonner'
import { api, useAsync, useTicker, type DeviceStatus, type DynamicPassword } from '@/lib/api'
import { cn, formatActivationCode, formatDynamicCode, formatTime, relativeTime } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

type Stage = 1 | 2 | 3
type BadgeVariant = 'default' | 'success' | 'warning' | 'destructive' | 'outline'

const STAGES: readonly Stage[] = [1, 2, 3]

const STAGE_LABEL: Record<Stage, string> = { 1: '阶段 1', 2: '阶段 2', 3: '阶段 3' }

const STATUS_LABEL: Record<DeviceStatus, string> = {
  pending: '待批准',
  active: '已激活',
  suspended: '已停用',
  retired: '已退役',
}

function statusVariant(status: DeviceStatus): BadgeVariant {
  if (status === 'active') return 'success'
  if (status === 'pending') return 'warning'
  if (status === 'suspended') return 'destructive'
  return 'outline'
}

function toStage(value: string): Stage {
  if (value === '2') return 2
  if (value === '3') return 3
  return 1
}

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/** 复制到剪贴板：成功后按钮短暂显示「已复制」。 */
function useCopy() {
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  async function copy(text: string, key: string, label: string) {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedKey(key)
      toast.success(`${label}已复制`)
      window.setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 1600)
    } catch {
      toast.error('复制失败，请手动选中文本复制')
    }
  }

  return { copiedKey, copy }
}

function EmptyHint({ text, hint }: { text: string; hint?: string }) {
  return (
    <div className="grid place-items-center gap-1.5 py-10 text-center">
      <KeyRound className="size-5 text-[var(--color-muted-foreground)]" />
      <div className="text-sm text-[var(--color-foreground)]">{text}</div>
      {hint ? <div className="text-xs text-[var(--color-muted-foreground)]">{hint}</div> : null}
    </div>
  )
}

function WarningNote({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-[var(--color-warning)] bg-[color-mix(in_oklch,var(--color-warning)_10%,transparent)] p-3 text-xs">
      <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-[var(--color-warning)]" />
      <p className="leading-relaxed text-[var(--color-muted-foreground)]">
        <span className="font-medium text-[var(--color-warning)]">{title}</span>
        {children}
      </p>
    </div>
  )
}

export function ActivationPage() {
  const now = useTicker(1000)
  const devicesState = useAsync(() => api.listDevices({ pageSize: 200 }), [])

  const [deviceId, setDeviceId] = useState('')
  const [stage, setStage] = useState<Stage>(1)

  // 已签发的仪器激活码：本地记录接收时刻，避免依赖客户端与服务器时钟一致
  const [issued, setIssued] = useState<{
    code: string
    issuedAt: number
    validForSeconds: number
    receivedAt: number
  } | null>(null)
  const [issueBusy, setIssueBusy] = useState(false)

  // 仪器激活码校验
  const [checkCode, setCheckCode] = useState('')
  const [checkBusy, setCheckBusy] = useState(false)
  const [checkResult, setCheckResult] = useState<{
    valid: boolean
    driftWindows: number | null
  } | null>(null)

  // 维护动态密码
  const [password, setPassword] = useState<{ data: DynamicPassword; fetchedAt: number } | null>(
    null,
  )
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordNonce, setPasswordNonce] = useState(0)

  const { copiedKey, copy } = useCopy()

  const rows = devicesState.data?.rows ?? []
  const selected = rows.find((d) => d.device_id === deviceId) ?? null
  const selectedHasSecret = Boolean(selected?.has_secret)
  const moreDevices = (devicesState.data?.total ?? rows.length) > rows.length

  // 换设备或换阶段后，旧的签发/校验结果立即作废
  useEffect(() => {
    setIssued(null)
    setCheckResult(null)
  }, [deviceId, stage])

  // 维护动态密码：仅在设备变化、手动刷新或窗口过期时请求（每次请求都会写操作日志）
  useEffect(() => {
    if (!deviceId || !selectedHasSecret) {
      setPassword(null)
      setPasswordError(null)
      setPasswordLoading(false)
      return
    }
    let alive = true
    setPasswordLoading(true)
    setPasswordError(null)
    api
      .dynamicPassword(deviceId)
      .then((d) => {
        if (alive) setPassword({ data: d, fetchedAt: Date.now() })
      })
      .catch((e: unknown) => {
        if (alive) {
          setPassword(null)
          setPasswordError(errMsg(e))
        }
      })
      .finally(() => {
        if (alive) setPasswordLoading(false)
      })
    return () => {
      alive = false
    }
  }, [deviceId, selectedHasSecret, passwordNonce])

  const pwRemaining = password
    ? Math.max(0, Math.ceil(password.data.secondsRemaining - (now - password.fetchedAt) / 1000))
    : 0
  const pwStep = password ? Math.max(1, password.data.stepSeconds) : 60
  const pwPercent = password ? Math.max(0, Math.min(100, (pwRemaining / pwStep) * 100)) : 0
  const pwExpired = password !== null && pwRemaining <= 0

  // 窗口切换时自动取下一个窗口的码；只用布尔依赖，过期期间不会重复触发
  useEffect(() => {
    if (!pwExpired) return
    setPasswordNonce((n) => n + 1)
  }, [pwExpired])

  const issueRemaining = issued
    ? Math.max(0, issued.validForSeconds - Math.floor((now - issued.receivedAt) / 1000))
    : 0
  const issueExpired = issued !== null && issueRemaining <= 0

  async function handleIssue() {
    if (!deviceId || issueBusy) return
    setIssueBusy(true)
    try {
      const res = await api.issueActivation(deviceId, stage)
      setIssued({
        code: res.code,
        issuedAt: res.issuedAt,
        validForSeconds: res.validForSeconds,
        receivedAt: Date.now(),
      })
      toast.success(`${STAGE_LABEL[stage]} 仪器激活码已签发，请在 ${res.validForSeconds} 秒内转达`)
    } catch (e) {
      toast.error(errMsg(e))
    } finally {
      setIssueBusy(false)
    }
  }

  async function handleCheck(e: React.FormEvent) {
    e.preventDefault()
    const code = checkCode.trim()
    if (!deviceId || code.length !== 7 || checkBusy) return
    setCheckBusy(true)
    try {
      const res = await api.checkActivation(deviceId, stage, code)
      setCheckResult(res)
      if (res.valid) toast.success('该仪器激活码有效')
      else toast.error('该仪器激活码无效或已过期')
    } catch (e) {
      toast.error(errMsg(e))
    } finally {
      setCheckBusy(false)
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">现场发码</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>选择设备</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {devicesState.loading && !devicesState.data ? (
            <div className="flex items-center gap-2 py-2 text-sm text-[var(--color-muted-foreground)]">
              <Loader2 className="size-4 animate-spin" />
              正在加载设备列表…
            </div>
          ) : devicesState.error ? (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-[var(--color-destructive)] p-3 text-xs text-[var(--color-destructive)]">
              <TriangleAlert className="size-3.5 shrink-0" />
              <span className="flex-1">设备列表加载失败：{devicesState.error}</span>
              <Button variant="ghost" size="sm" onClick={devicesState.reload}>
                <RefreshCw className="size-3.5" />
                重试
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <div className="py-4 text-sm text-[var(--color-muted-foreground)]">
              暂无设备。请先到{' '}
              <Link
                to="/devices"
                className="text-[var(--color-primary)] underline underline-offset-4"
              >
                设备管理
              </Link>{' '}
              登记设备；设备上报公钥后会自动签发设备密钥。
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="activation-device">设备</Label>
                <Select value={deviceId} onValueChange={setDeviceId}>
                  <SelectTrigger id="activation-device">
                    <SelectValue placeholder="选择设备…" />
                  </SelectTrigger>
                  <SelectContent>
                    {rows.map((device) => {
                      const usable = Boolean(device.has_secret)
                      return (
                        <SelectItem key={device.id} value={device.device_id} disabled={!usable}>
                          <span
                            className={cn(
                              'font-mono-token text-xs',
                              !usable && 'text-[var(--color-muted-foreground)]',
                            )}
                          >
                            {device.device_name} ({device.device_id})
                            {usable ? '' : ' · 未签发设备密钥'}
                          </span>
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[var(--color-muted-foreground)]">
                  <span>共 {rows.length} 台可选设备</span>
                  {moreDevices ? (
                    <span>（仅显示最近 200 台，可用设备管理页搜索更早的设备）</span>
                  ) : null}
                </div>
              </div>

              {selected ? (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-[var(--color-border)] bg-[var(--color-muted)]/40 px-3 py-2.5 text-xs">
                  <span className="flex items-center gap-1.5 font-medium">
                    <Smartphone className="size-3.5" />
                    {selected.device_name}
                  </span>
                  <span className="font-mono-token text-[var(--color-muted-foreground)]">
                    {selected.device_id}
                  </span>
                  <Badge variant={statusVariant(selected.status)}>
                    {STATUS_LABEL[selected.status]}
                  </Badge>
                  <Badge variant={selectedHasSecret ? 'success' : 'warning'}>
                    {selectedHasSecret ? '已签发设备密钥' : '未签发设备密钥'}
                  </Badge>
                  <span className="text-[var(--color-muted-foreground)]">
                    最后在线 {relativeTime(selected.last_seen_at)}
                  </span>
                </div>
              ) : null}

              {selected && !selectedHasSecret ? (
                <WarningNote title="该设备未签发设备密钥。">
                  仪器激活码与维护动态密码都基于设备密钥计算。设备上报公钥后会自动签发设备密钥；若设备密钥已被吊销，请到设备详情页的「设备密钥」页签点击「立即签发设备密钥」。
                </WarningNote>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="size-4" />
              仪器激活码
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!selected ? (
              <EmptyHint text="请先在上方选择设备" hint="选择设备后即可签发仪器激活码" />
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label>阶段</Label>
                  <Tabs value={String(stage)} onValueChange={(v) => setStage(toStage(v))}>
                    <TabsList className="w-full">
                      {STAGES.map((s) => (
                        <TabsTrigger key={s} value={String(s)} className="flex-1">
                          {STAGE_LABEL[s]}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>
                </div>

                <Button
                  onClick={handleIssue}
                  disabled={issueBusy || !selectedHasSecret}
                  className="w-full"
                >
                  {issueBusy ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <KeyRound className="size-4" />
                  )}
                  {issued ? '重新签发仪器激活码' : '签发仪器激活码'}
                </Button>

                {issued ? (
                  <div
                    className={cn(
                      'space-y-3 rounded-lg border p-4',
                      issueExpired
                        ? 'border-[var(--color-destructive)]'
                        : 'border-[var(--color-primary)]',
                    )}
                  >
                    <div className="flex items-center justify-between text-xs text-[var(--color-muted-foreground)]">
                      <span>
                        {STAGE_LABEL[stage]} · 窗口签发于 {formatTime(issued.issuedAt)}
                      </span>
                      <span
                        className={cn(
                          'font-medium tabular-nums',
                          issueExpired
                            ? 'text-[var(--color-destructive)]'
                            : 'text-[var(--color-success)]',
                        )}
                      >
                        {issueExpired ? '已过期' : `剩余 ${issueRemaining} 秒`}
                      </span>
                    </div>
                    <div
                      className={cn(
                        'font-mono-token text-center text-4xl font-semibold',
                        issueExpired
                          ? 'text-[var(--color-muted-foreground)] line-through'
                          : 'text-[var(--color-primary)]',
                      )}
                    >
                      {formatActivationCode(issued.code)}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => copy(issued.code, 'activation', '仪器激活码')}
                    >
                      {copiedKey === 'activation' ? (
                        <Check className="size-4" />
                      ) : (
                        <Copy className="size-4" />
                      )}
                      {copiedKey === 'activation' ? '已复制' : '复制仪器激活码'}
                    </Button>
                  </div>
                ) : (
                  <div className="grid place-items-center rounded-lg border border-dashed border-[var(--color-border)] py-6 text-xs text-[var(--color-muted-foreground)]">
                    尚未签发仪器激活码
                  </div>
                )}

                <WarningNote title="仪器激活码每 60 秒变化一次。">
                  上面这串码只在当前窗口有效，必须在倒计时结束前通过电话/对讲机等可靠渠道转达给现场，并由现场在设备上输入；
                  窗口切换后该码立即失效，需要重新签发（设备端允许 ±1 个窗口的漂移）。
                </WarningNote>

                <Separator />

                <form onSubmit={handleCheck} className="space-y-2">
                  <Label htmlFor="activation-check">校验仪器激活码</Label>
                  <div className="flex gap-2">
                    <Input
                      id="activation-check"
                      value={checkCode}
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="输入设备上显示的 7 位数字"
                      className="font-mono-token"
                      onChange={(e) => setCheckCode(e.target.value.replace(/\D/g, '').slice(0, 7))}
                    />
                    <Button
                      type="submit"
                      variant="secondary"
                      disabled={checkBusy || checkCode.length !== 7 || !selectedHasSecret}
                    >
                      {checkBusy ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <ScanLine className="size-4" />
                      )}
                      校验
                    </Button>
                  </div>
                  <p className="text-[11px] text-[var(--color-muted-foreground)]">
                    按当前选中的「{STAGE_LABEL[stage]}」校验；用于现场排障，不会改变设备激活状态。
                  </p>

                  {checkResult ? (
                    <div
                      className={cn(
                        'flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm',
                        checkResult.valid
                          ? 'border-[var(--color-success)] text-[var(--color-success)]'
                          : 'border-[var(--color-destructive)] text-[var(--color-destructive)]',
                      )}
                    >
                      {checkResult.valid ? (
                        <CircleCheck className="size-4" />
                      ) : (
                        <CircleX className="size-4" />
                      )}
                      <span className="font-medium">{checkResult.valid ? '有效' : '无效'}</span>
                      {checkResult.valid && checkResult.driftWindows !== null ? (
                        <span className="text-xs text-[var(--color-muted-foreground)]">
                          （与当前窗口相差 {checkResult.driftWindows} 个窗口，±1 内视为有效）
                        </span>
                      ) : null}
                      {!checkResult.valid ? (
                        <span className="text-xs text-[var(--color-muted-foreground)]">
                          （码已过期、阶段不符或设备密钥已轮换）
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </form>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Timer className="size-4" />
              维护动态密码
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!selected ? (
              <EmptyHint text="请先在上方选择设备" hint="选择设备后即可查看维护动态密码" />
            ) : !selectedHasSecret ? (
              <WarningNote title="该设备未签发设备密钥。">
                没有设备密钥就无法计算维护动态密码。设备上报公钥后会自动签发设备密钥；若设备密钥已被吊销，请到设备详情页的「设备密钥」页签点击「立即签发设备密钥」。
              </WarningNote>
            ) : passwordError ? (
              <div className="space-y-3">
                <div className="flex items-start gap-2 rounded-md border border-[var(--color-destructive)] p-3 text-xs text-[var(--color-destructive)]">
                  <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
                  <span>获取维护动态密码失败：{passwordError}</span>
                </div>
                <Button variant="outline" size="sm" onClick={() => setPasswordNonce((n) => n + 1)}>
                  <RefreshCw className="size-4" />
                  重试
                </Button>
              </div>
            ) : !password ? (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-[var(--color-muted-foreground)]">
                <Loader2 className="size-4 animate-spin" />
                正在计算维护动态密码…
              </div>
            ) : (
              <>
                <div className="space-y-3 rounded-lg border border-[var(--color-border)] p-4">
                  <div className="font-mono-token text-center text-5xl font-semibold text-[var(--color-foreground)]">
                    {formatDynamicCode(password.data.current)}
                  </div>
                  <div className="countdown-track">
                    <div className="countdown-fill" style={{ width: `${pwPercent}%` }} />
                  </div>
                  <div className="flex items-center justify-between text-xs text-[var(--color-muted-foreground)]">
                    <span className="tabular-nums">
                      {passwordLoading ? '正在刷新…' : `${pwRemaining} 秒后刷新`}
                    </span>
                    <span className="font-mono-token">窗口 #{password.data.window}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    onClick={() => copy(password.data.current, 'password', '维护动态密码')}
                  >
                    {copiedKey === 'password' ? (
                      <Check className="size-4" />
                    ) : (
                      <Copy className="size-4" />
                    )}
                    {copiedKey === 'password' ? '已复制' : '复制维护动态密码'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setPasswordNonce((n) => n + 1)}
                    disabled={passwordLoading}
                  >
                    {passwordLoading ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <RefreshCw className="size-4" />
                    )}
                    刷新
                  </Button>
                </div>

                <div className="rounded-md border border-[var(--color-border)] px-3 py-2.5">
                  <div className="text-[11px] text-[var(--color-muted-foreground)]">
                    下一个窗口的维护动态密码（倒计时结束后自动生效，可用于临界时提前告知）
                  </div>
                  <div className="font-mono-token mt-1 text-xl text-[var(--color-muted-foreground)]">
                    {formatDynamicCode(password.data.next)}
                  </div>
                </div>

                <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-[var(--color-muted-foreground)]">
                  <Info className="mt-0.5 size-3 shrink-0" />
                  维护动态密码每 60 秒自动刷新，界面只在窗口切换时重新请求；每次获取都会写入操作日志
                  （dynamic_password.view），请勿频繁手动刷新。
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
